import { Alert, useTheme } from "@mui/material";
import * as React from "react";

import {
  MetadataForm,
  MetadataView,
  type Metadata,
  type MetadataDiff,
} from "@/shared/components/Metadata";
import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { Device } from "@/shared/domain/devices";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";
import { type MetadataChangeset } from "@/shared/domain/metadataChangeset";

interface UpdateDeviceMetadataProps {
  device: Device;
  resourceOwnerId: string;
  readonly?: boolean;
  onMetadataUpdated?: (device: Device) => void;
  onError?: (error: Error) => void;
}

export const UpdateDeviceMetadata: React.FC<UpdateDeviceMetadataProps> = ({
  device,
  resourceOwnerId,
  readonly = false,
  onMetadataUpdated,
  onError,
}) => {
  const theme = useTheme();
  const updateDeviceMutation = useUpdateDevice();

  const applyChangeset = (changeset: MetadataChangeset) => {
    updateDeviceMutation.mutate(
      {
        deviceId: device.device_id,
        resourceOwnerId,
        updates: { metadata_changeset: changeset },
      },
      {
        onSuccess: (updatedDevice) => {
          onMetadataUpdated?.(updatedDevice);
        },
        onError: (error) => {
          onError?.(error);
        },
      },
    );
  };

  const addMetadata = (key: string, value: unknown) => {
    applyChangeset({ put_fields: { [key]: value } });
  };

  const updateMetadata = (nextMetadata: Metadata, diff?: MetadataDiff) => {
    // Edits made in the JSON editor arrive as `keysUpdated`. Deletes are left
    // to `removeMetadata` below: MetadataView fires this callback *and*
    // `onDeleteMetadata` for a single chip-delete, so acting on `keysDeleted`
    // here would issue two requests for the one gesture.
    if (!diff?.keysUpdated?.length) {
      return;
    }
    const putFields: Record<string, unknown> = {};
    for (const key of diff.keysUpdated) {
      putFields[key] = nextMetadata[key];
    }
    applyChangeset({ put_fields: putFields });
  };

  const removeMetadata = (key: string) => {
    applyChangeset({ remove_fields: [key] });
  };

  return (
    <>
      {readonly && Object.keys(device.metadata).length === 0 && <>None</>}
      <MetadataView
        key={device.device_id}
        metadata={device.metadata}
        onChange={updateMetadata}
        onDeleteMetadata={removeMetadata}
        // Suppresses both the editor and the delete button inside MetadataView,
        // so the handlers above need no readonly guard of their own.
        disabled={readonly || updateDeviceMutation.isPending}
      />
      {readonly === false && (
        <MetadataForm
          key={`${device.device_id}-form`}
          metadata={device.metadata}
          autocompleteType={AutocompleteType.GenericMetadataKeys}
          onAddMetadata={addMetadata}
          disabled={updateDeviceMutation.isPending}
          isLoading={updateDeviceMutation.isPending}
        />
      )}

      {updateDeviceMutation.isError && (
        <Alert
          onClose={() => updateDeviceMutation.reset()}
          severity="error"
          sx={{ marginTop: theme.spacing(1) }}
        >
          {updateDeviceMutation.error.message}
        </Alert>
      )}
    </>
  );
};
